import MCheckbox from '../MCheckbox';
import { AppCellContext, AppColumnDef, AppTable } from './appTable';
import { getColHeaderFilters } from './tableUtilsNew';

export const SELECTION_COL_KEY = '_SELECT_';
export const ACTION_COLUMN_KEY = '_ACTION_';

interface CheckboxHeaderSelectCellProps<T extends object> {
  table: AppTable<T>;
}

export const CheckboxHeaderSelectCell = <T extends object>({
  table,
}: CheckboxHeaderSelectCellProps<T>) => {
  const selectionDisabled = table.options.meta?.selectionDisabled || false;
  const isAllRowsSelected = table.getIsAllRowsSelected();
  // `getIsSomeRowsSelected()` means "at least one row is selected" in v9 (it
  // stayed false at full selection in v8), so it must be gated on the
  // all-selected check or the box would remain indeterminate once everything
  // is selected.
  const isIndeterminate = table.getIsSomeRowsSelected() && !isAllRowsSelected;
  return (
    <MCheckbox
      isChecked={isAllRowsSelected}
      isIndeterminate={isIndeterminate}
      isDisabled={selectionDisabled}
      onChange={table.getToggleAllRowsSelectedHandler()}
    />
  );
};

export const CheckboxSelectCell = <TData extends object, TValue>({
  row,
  table,
}: AppCellContext<TData, TValue>) => {
  const selectionDisabled = table.options.meta?.selectionDisabled || false;
  return (
    <MCheckbox
      isChecked={row.getIsSelected()}
      isDisabled={selectionDisabled || !row.getCanSelect()}
      onChange={row.getToggleSelectedHandler()}
    />
  );
};

export const SELECTION_COL: AppColumnDef<any, any> = {
  id: SELECTION_COL_KEY,
  ...getColHeaderFilters('SELECTION'),
  header: CheckboxHeaderSelectCell,
  cell: CheckboxSelectCell,
  accessorFn: (originalRow) => originalRow.id,
  size: 55,
  enableSorting: false,
  enablePinning: true,
  enableResizing: false,
};

/**
 * Creates a reusable action column definition for data tables
 * @param actionBodyTemplate - Function to render action menu in each row
 * @returns Column definition for the action column
 */
export const createActionColumn = <TData extends object>(
  actionBodyTemplate: (rowData: TData) => React.ReactNode,
): AppColumnDef<TData, unknown> => ({
  id: ACTION_COLUMN_KEY,
  header: '',
  cell: (context) => {
    return actionBodyTemplate(context.row.original);
  },
  size: 75,
  enableSorting: false,
  enablePinning: true,
  enableResizing: false,
  meta: {
    cellStyles: {
      justifyContent: 'center',
    },
  },
});
